import { useEffect, useMemo, useRef } from 'react';
import './MarketPriceChart.css';

const colorByTone = {
  crypto: '#12965e',
  gold: '#b98213',
  stock: '#315fbb',
};

function fallbackPath(points) {
  if (!points.length) return '';
  const width = 720;
  const height = 210;
  const values = points.map((point) => Number(point.value ?? point.close)).filter(Number.isFinite);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const spread = max - min || 1;
  return points.map((point, index) => {
    const value = Number(point.value ?? point.close);
    const x = points.length === 1 ? 0 : (index / (points.length - 1)) * width;
    const y = height - ((value - min) / spread) * (height - 18) - 9;
    return `${index === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(' ');
}

function ChartFallback({ data, tone, label }) {
  const path = fallbackPath(data);
  const stroke = colorByTone[tone] || colorByTone.crypto;

  return (
    <svg className="market-price-chart-fallback" viewBox="0 0 720 230" preserveAspectRatio="none" role="img" aria-label={label}>
      <defs>
        <linearGradient id={`market-price-chart-fill-${tone}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.28" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={`${path} L720 230 L0 230 Z`} fill={`url(#market-price-chart-fill-${tone})`} />
      <path d={path} fill="none" stroke={stroke} strokeWidth="3" />
    </svg>
  );
}

export default function MarketPriceChart({
  data,
  tone = 'crypto',
  height = 260,
  loading = false,
  error = '',
  label = 'Price chart',
  valuePrefix = '$',
}) {
  const containerRef = useRef(null);
  const isTest = process.env.NODE_ENV === 'test';
  const points = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  useEffect(() => {
    if (isTest || !containerRef.current || !points.length) return undefined;

    const container = containerRef.current;
    let chart;
    let cancelled = false;
    let cleanup = () => {};
    const lineColor = colorByTone[tone] || colorByTone.crypto;

    import('lightweight-charts').then(({ AreaSeries, createChart }) => {
      if (cancelled || !containerRef.current) return;

      chart = createChart(container, {
        width: container.clientWidth || 720,
        height,
        layout: {
          background: { color: 'rgba(255, 255, 255, 0)' },
          textColor: '#82909a',
        },
        grid: {
          vertLines: { color: 'rgba(24, 33, 39, 0.055)' },
          horzLines: { color: 'rgba(24, 33, 39, 0.075)' },
        },
        rightPriceScale: {
          borderColor: 'rgba(24, 33, 39, 0.10)',
          scaleMargins: { top: 0.12, bottom: 0.14 },
        },
        timeScale: {
          borderColor: 'rgba(24, 33, 39, 0.10)',
          timeVisible: false,
          secondsVisible: false,
        },
        crosshair: { mode: 0 },
        localization: {
          priceFormatter: (price) => `${valuePrefix}${Number(price).toLocaleString(undefined, { maximumFractionDigits: price >= 100 ? 2 : 4 })}`,
        },
      });

      const series = chart.addSeries(AreaSeries, {
        lineColor,
        topColor: `${lineColor}47`,
        bottomColor: `${lineColor}05`,
        lineWidth: 2,
        priceLineColor: lineColor,
      });

      series.setData(points.map((point) => ({
        time: point.time,
        value: Number(point.value ?? point.close),
      })).filter((point) => Number.isFinite(point.value)));

      chart.timeScale().fitContent();

      const handleResize = () => {
        chart.applyOptions({ width: container.clientWidth || 720 });
        chart.timeScale().fitContent();
      };

      window.addEventListener('resize', handleResize);
      cleanup = () => {
        window.removeEventListener('resize', handleResize);
        chart.remove();
      };
    });

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [height, isTest, points, tone, valuePrefix]);

  if (loading && !points.length) {
    return (
      <div className="market-price-chart-state" style={{ minHeight: height }}>
        正在加载行情图表…
      </div>
    );
  }

  if (!points.length) {
    if (error) {
      console.warn('[MarketPriceChart] Chart data unavailable', { label, error });
    }
    return (
      <div className="market-price-chart-state" style={{ minHeight: height }}>
        图表数据暂时不可用
      </div>
    );
  }

  if (isTest) {
    return <ChartFallback data={points} tone={tone} label={label} />;
  }

  return <div ref={containerRef} className="market-price-chart-canvas" style={{ height }} aria-label={label} />;
}
